// @flow

import type WorkflowResource from 'esm-web-pro/api/resources/WorkflowResource';
import type { ApiClient, WebSocketClient } from 'speed-js-core';
import WorkflowError, { ERR_INVALID_ACTION, ERR_WEBSOCKET_NOT_SUBSCRIBED } from './errors';


let GLOBAL_ACTION_HANDLERS: {
    [string]: {
        async: boolean,
        func: (data: any, context: Object) => any,
    }
} = {};


export default class WorkflowManager {
    context: {
        apiClient: ApiClient,
        wsClient: WebSocketClient,
        [string]: any,
    };

    workflows: {
        [execId: string]: {
            actionHandlers: {
                [string]: {
                    async: boolean,
                    func: (data: { executionId: string, [string]: any }, context: Object) => any,
                },
            },
            state: string,
        }
    } = {};


    apiResource: WorkflowResource;

    _subscribedToWS = false;

    static registerGlobalActionHandlers(actions: {
        [string]: {
            async: boolean,
            func: (data: { executionId: string, [string]: any }, context: Object) => any,
        }
    }) {
        GLOBAL_ACTION_HANDLERS = { ...GLOBAL_ACTION_HANDLERS, ...actions };
    }

    constructor(context: {
        apiClient: ApiClient,
        wsClient: WebSocketClient,
        [string]: any,
    }, settings: { workflowResource: string } = {
        workflowResource: 'workflow',
    }) {
        this.context = context;
        this.apiResource = context.apiClient.resources[settings.workflowResource];

        this.subscribeToFlowActions();
    }

    subscribeToFlowActions() {
        this.context.wsClient.onClose(() => {
            this._subscribedToWS = false;
        });
        this.context.wsClient.onSubscribe('workflows', () => {
            this._subscribedToWS = true;
        });

        this.context.wsClient.subscribe({
            workflows: (data: { action: string, execution_id: string, [string]: any }) => {
                const { action, ...rest } = data;
                this.handleAction(action, rest);
            },
        });
    }

    /*
     * Start a new wokrflow. TODO: doc
     *
     * @param path
     * @param data
     * @param actionHandlers
     * @param {bool} forceStart Start the flow regardless if the manager has subscribed to the websocket
     * @return {Promise}
     */
    startFlow = (path: string, data: Object, actionHandlers?: {
        [string]: {
            async: boolean,
            func: (data: { executionId: string, [string]: any }, context: Object) => any,
        },
    }, forceStart: boolean = false): Promise<Object> => {
        if (this._subscribedToWS || forceStart) {
            return this.apiResource.start(
                path, data,
                { ws_connection_id: this.context.wsClient.connectionId },
            ).then((response: { id: string, state: string, }) => {
                this.workflows[response.id] = {
                    state: response.state,
                    actionHandlers: actionHandlers || {},
                };

                return response;
            });
        }

        // Wait 0.1 second, max 100 times, to check if we have subscribed to the websocket and start the flow only when
        // we are subscribed, otherwise throw an error, since all flow actions come from the websocket and if it is not
        // working, there's not point of starting the flow
        return new Promise((resolve) => {
            let retries = 500;

            const wait = () => {
                if (retries > 0) {
                    console.log('Waiting to subscribe to websocket');
                    setTimeout(() => {
                        if (this._subscribedToWS) {
                            this.apiResource.start(
                                path, data,
                                { ws_connection_id: this.context.wsClient.connectionId },
                            ).then((response: { id: string, state: string, }) => {
                                this.workflows[response.id] = {
                                    state: response.state,
                                    actionHandlers: actionHandlers || {},
                                };

                                resolve(response);
                            });
                        } else {
                            retries -= 1;
                            wait();
                        }
                    }, 100);
                } else {
                    throw new WorkflowError(
                        'Not connected or subscribed to the "workflow" websocket group and flow actions wont work.'
                        + 'If you still want to start the flow you can pass true for the forceStart flag.',
                        ERR_WEBSOCKET_NOT_SUBSCRIBED,
                    );
                }
            };
            wait();
        });
    };

    resumeFlow = (executionId: string, data: Object, actionHandlers?: {
        [string]: {
            async: boolean,
            func: (data: { executionId: string, [string]: any }, context: Object) => any,
        },
    }, forceResume: boolean = false): Promise<Object> => {
        if (!this.workflows[executionId]) {
            this.workflows[executionId] = { actionHandlers: {}, state: '' };
        }

        if (actionHandlers) {
            Object.assign(this.workflows[executionId].actionHandlers, actionHandlers);
        }

        if (this._subscribedToWS || forceResume) {
            return this.apiResource.resume(
                executionId, data,
                { ws_connection_id: this.context.wsClient.connectionId },
            ).then((response: { id: string, state: string, }) => {
                this.workflows[response.id].state = response.state;

                return response;
            });
        }

        // Wait 1 second, max 10 times, to check if we have subscribed to the websocket and resume the flow only when
        // we are subscribed, otherwise throw an error, since all flow actions come from the websocket and if it is not
        // working, there's not point of resuming the flow
        return new Promise((resolve) => {
            let retries = 10;

            const wait = () => {
                if (retries > 0) {
                    console.log('Waiting 1 second to subscribe to websocket');
                    setTimeout(() => {
                        if (this._subscribedToWS) {
                            this.apiResource.resume(
                                executionId, data,
                                { ws_connection_id: this.context.wsClient.connectionId },
                            ).then(
                                (response: { id: string, state: string, }) => {
                                    this.workflows[response.id].state = response.state;

                                    resolve(response);
                                },
                            );
                        } else {
                            retries -= 1;
                            wait();
                        }
                    }, 1000);
                } else {
                    throw new WorkflowError(
                        'Not connected or subscribed to the "workflow" websocket group and flow actions wont work.'
                        + 'If you still want to resume the flow you can pass true for the forceResume flag.',
                        ERR_WEBSOCKET_NOT_SUBSCRIBED,
                    );
                }
            };
            wait();
        });
    };

    addActionHandler = (
        executionId: string, action: string,
        func: (data: { executionId: string, [string]: any }, context: Object) => any, async: boolean = false,
    ) => {
        if (!this.workflows[executionId]) {
            this.workflows[executionId] = { state: '', actionHandlers: {} };
        }
        this.workflows[executionId].actionHandlers[action] = { async, func };
    };

    removeActionHandler = (executionId: string, action: string) => {
        if (this.workflows[executionId] && this.workflows[executionId].actionHandlers[action]) {
            delete this.workflows[executionId].actionHandlers[action];
        }
    };

    handleAction = (name: string, data: { execution_id: string, [string]: any }): any => {
        const { execution_id: executionId } = data;
        let action;

        if (this.workflows[executionId] && this.workflows[executionId].actionHandlers[name]) {
            action = this.workflows[executionId].actionHandlers[name];
        } else {
            action = GLOBAL_ACTION_HANDLERS[name];
        }

        if (!action) {
            throw new WorkflowError(`Received unknown action "${name}"`, ERR_INVALID_ACTION);
        }

        if (action.async) {
            return new Promise((resolve) => {
                const result = action.func(data, this.context);
                resolve(result);
            });
        }

        return action.func(data, this.context);
    }
}
